import React, { useState } from 'react';
import {
  Crosshair,
  TrendingUp,
  Minus,
  MoreVertical,
  Square,
  Type,
  Ruler,
  MousePointer,
  PenTool,
  Circle,
  Triangle,
  ArrowUpRight,
  Percent,
  GitBranch,
  Layers,
  Lock,
  Unlock,
  Trash2,
  Eye,
  EyeOff,
  Settings
} from 'lucide-react';

interface ChartToolbarProps {
  onToolSelect?: (tool: string) => void;
}

interface ToolButton {
  id: string;
  icon: React.ReactNode;
  tooltip: string;
  group?: string;
}

const ChartToolbar: React.FC<ChartToolbarProps> = ({ onToolSelect }) => {
  const [selectedTool, setSelectedTool] = useState('cursor');
  const [showDrawings, setShowDrawings] = useState(true);
  const [lockDrawings, setLockDrawings] = useState(false);

  const handleToolClick = (toolId: string) => {
    setSelectedTool(toolId);
    onToolSelect?.(toolId);
  };

  const tools: ToolButton[] = [
    { id: 'cursor', icon: <MousePointer size={18} />, tooltip: 'Cursor', group: 'selection' },
    { id: 'crosshair', icon: <Crosshair size={18} />, tooltip: 'Crosshair', group: 'selection' },
  ];

  const lineTools: ToolButton[] = [
    { id: 'trendline', icon: <TrendingUp size={18} />, tooltip: 'Trend Line', group: 'lines' },
    { id: 'horizontal', icon: <Minus size={18} />, tooltip: 'Horizontal Line', group: 'lines' },
    { id: 'vertical', icon: <MoreVertical size={18} />, tooltip: 'Vertical Line', group: 'lines' },
    { id: 'ray', icon: <ArrowUpRight size={18} />, tooltip: 'Ray', group: 'lines' },
    { id: 'channel', icon: <GitBranch size={18} />, tooltip: 'Parallel Channel', group: 'lines' },
  ];

  const fibTools: ToolButton[] = [
    { id: 'fib-retracement', icon: <Percent size={18} />, tooltip: 'Fibonacci Retracement', group: 'fibonacci' },
    { id: 'fib-extension', icon: <Layers size={18} />, tooltip: 'Fibonacci Extension', group: 'fibonacci' },
  ];

  const shapeTools: ToolButton[] = [
    { id: 'rectangle', icon: <Square size={18} />, tooltip: 'Rectangle', group: 'shapes' },
    { id: 'circle', icon: <Circle size={18} />, tooltip: 'Circle', group: 'shapes' },
    { id: 'triangle', icon: <Triangle size={18} />, tooltip: 'Triangle', group: 'shapes' },
  ];

  const annotationTools: ToolButton[] = [
    { id: 'text', icon: <Type size={18} />, tooltip: 'Text', group: 'annotation' },
    { id: 'brush', icon: <PenTool size={18} />, tooltip: 'Brush', group: 'annotation' },
    { id: 'ruler', icon: <Ruler size={18} />, tooltip: 'Measure', group: 'annotation' },
  ];

  const renderToolButton = (tool: ToolButton) => (
    <button
      key={tool.id}
      onClick={() => handleToolClick(tool.id)}
      className={`
        w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200
        ${selectedTool === tool.id
          ? 'bg-blue-500 text-white shadow-md'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
        }
      `}
      title={tool.tooltip}
    >
      {tool.icon}
    </button>
  );

  const renderDivider = () => (
    <div className="w-8 h-px bg-slate-200 my-1 mx-auto" />
  );

  return (
    <div className="flex flex-col items-center py-2 h-full">
      <div className="flex flex-col items-center gap-1">
        {tools.map(renderToolButton)}
      </div>

      {renderDivider()}

      <div className="flex flex-col items-center gap-1">
        {lineTools.map(renderToolButton)}
      </div>

      {renderDivider()}

      <div className="flex flex-col items-center gap-1">
        {fibTools.map(renderToolButton)}
      </div>

      {renderDivider()}

      <div className="flex flex-col items-center gap-1">
        {shapeTools.map(renderToolButton)}
      </div>

      {renderDivider()}

      <div className="flex flex-col items-center gap-1">
        {annotationTools.map(renderToolButton)}
      </div>

      <div className="flex-1" />

      <div className="flex flex-col items-center gap-1 mt-2">
        <button
          onClick={() => setShowDrawings(!showDrawings)}
          className={`
            w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200
            ${showDrawings ? 'text-blue-500' : 'text-slate-400'}
            hover:bg-slate-100
          `}
          title={showDrawings ? 'Hide Drawings' : 'Show Drawings'}
        >
          {showDrawings ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>

        <button
          onClick={() => setLockDrawings(!lockDrawings)}
          className={`
            w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200
            ${lockDrawings ? 'text-orange-500' : 'text-slate-400'}
            hover:bg-slate-100
          `}
          title={lockDrawings ? 'Unlock Drawings' : 'Lock Drawings'}
        >
          {lockDrawings ? <Lock size={18} /> : <Unlock size={18} />}
        </button>

        <button
          className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-500 transition-all duration-200"
          title="Delete All Drawings"
        >
          <Trash2 size={18} />
        </button>

        <button
          className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-all duration-200"
          title="Settings"
        >
          <Settings size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChartToolbar;
